import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import OverviewPage from './components/OverviewPage';
import PricingPage from './components/PricingPage';
import FAQPage from './components/FAQPage';
import HowItWorksPage from './components/HowItWorksPage';
import DashboardPage from './components/DashboardPage';
import OptimizeLocalPage from './components/OptimizeLocalPage';
import CreatePage from './components/CreatePage';
// import CampaignsPage from './components/CampaignsPage'; // Hidden for now, keeping file for later use
import ChatBotPage from './components/ChatBotPage';
import StrategyCanvas from './components/StrategyCanvas';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import TermsOfServicePage from './components/TermsOfServicePage';
import Footer from './components/Footer';
import LoginOverlay from './components/LoginOverlay';
import { useDebounce } from './hooks/useDebounce';
import { WAITLIST_ONLY, PUBLIC_NAV_ITEMS } from './config';
import authService from './services/authService';
import 'reactflow/dist/style.css';
import './App.css';
import './mobile-styles.css';

// Suppress ResizeObserver warnings
if (typeof window !== 'undefined') {
  window.addEventListener('error', e => {
    if (e.message === 'ResizeObserver loop completed with undelivered notifications.' || 
        e.message === 'ResizeObserver loop limit exceeded') {
      const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
      const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
      if (resizeObserverErr) {
        resizeObserverErr.setAttribute('style', 'display: none');
      }
      if (resizeObserverErrDiv) {
        resizeObserverErrDiv.setAttribute('style', 'display: none');
      }
    }
  });
}

function AppContent() {
  const {
    isAuthenticated,
    currentBusiness,
    userProfile,
    loading,
    error,
    login,
    joinWaitlist,
    logout,
  } = useAuth();

  // Waitlist-only: never treat as logged in (defense in depth vs context)
  const authed = WAITLIST_ONLY ? false : !!isAuthenticated;

  const [map, setMap] = useState(null);
  const [center, setCenter] = useState({ lat: 40.7128, lng: -74.0060 });
  const [placesService, setPlacesService] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(true);
  
  // Search functionality
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSearchResult, setSelectedSearchResult] = useState(null);
  const [competitorResults, setCompetitorResults] = useState([]);
  const [showCompetitors, setShowCompetitors] = useState(false);
  const [isSearchingCompetitors, setIsSearchingCompetitors] = useState(false);
  const [activeNavItem, setActiveNavItem] = useState('overview');
  // Don't auto-block the page with overlay on load — marketing first; CTA opens waitlist
  const [showLoginOverlay, setShowLoginOverlay] = useState(false);
  const [strategyPrompt, setStrategyPrompt] = useState('');
  
  // Debounced search query for performance
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Scrub stale tokens + bounce product navs back to home
  useEffect(() => {
    if (!WAITLIST_ONLY) return;
    authService.clearAllAuthStorage();
    if (!PUBLIC_NAV_ITEMS.has(activeNavItem)) {
      setActiveNavItem('overview');
      setShowLoginOverlay(true);
    }
  }, [activeNavItem]);

  const setActiveNavItemSafe = useCallback((item) => {
    if (WAITLIST_ONLY && !PUBLIC_NAV_ITEMS.has(item)) {
      setActiveNavItem('overview');
      setShowLoginOverlay(true);
      return;
    }
    setActiveNavItem(item);
  }, []);

  const mapContainerStyle = useMemo(() => ({
    width: '100%',
    height: '400px',
  }), []);

  const mapOptions = useMemo(() => ({
    disableDefaultUI: true,
    zoomControl: true,
    mapTypeControl: false,
    scaleControl: false,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: false,
    gestureHandling: 'greedy',
    clickableIcons: false,
    backgroundColor: '#f8f8f8',
    restriction: {
      latLngBounds: {
        north: 85,
        south: -85,
        west: -180,
        east: 180
      }
    },
    styles: [
      {
        featureType: "all",
        elementType: "labels",
        stylers: [{ visibility: "off" }]
      },
      {
        featureType: "administrative",
        elementType: "all",
        stylers: [{ visibility: "off" }]
      },
      {
        featureType: "landscape",
        elementType: "geometry.fill",
        stylers: [{ color: "#f8f8f8" }]
      },
      {
        featureType: "poi",
        elementType: "all",
        stylers: [{ visibility: "off" }]
      },
      {
        featureType: "road",
        elementType: "all",
        stylers: [{ visibility: "off" }]
      },
      {
        featureType: "road.highway",
        elementType: "all",
        stylers: [{ visibility: "off" }]
      },
      {
        featureType: "road.arterial",
        elementType: "all",
        stylers: [{ visibility: "off" }]
      },
      {
        featureType: "road.local",
        elementType: "all",
        stylers: [{ visibility: "off" }]
      },
      {
        featureType: "transit",
        elementType: "all",
        stylers: [{ visibility: "off" }]
      },
      {
        featureType: "water",
        elementType: "geometry.fill",
        stylers: [{ color: "#e6e6e6" }]
      },
      {
        featureType: "water",
        elementType: "labels",
        stylers: [{ visibility: "off" }]
      }
    ]
  }), []);

  const onLoad = useCallback((map) => {
    setMap(map);
    const service = new window.google.maps.places.PlacesService(map);
    setPlacesService(service);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Debounced search functionality
  const searchBusinesses = useCallback(async (query) => {
    if (!query || !placesService) return;

    const request = {
      query: query,
      fields: ['place_id', 'name', 'geometry', 'formatted_address', 'rating', 'user_ratings_total', 'price_level', 'photos', 'opening_hours', 'website', 'formatted_phone_number']
    };

    placesService.textSearch(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
        const formattedResults = results.slice(0, 10).map(place => ({
          id: place.place_id,
          name: place.name,
          address: place.formatted_address,
          rating: place.rating || 0,
          reviewCount: place.user_ratings_total || 0,
          priceLevel: place.price_level || 0,
          position: {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          },
          photo: place.photos?.[0]?.getUrl({maxWidth: 300, maxHeight: 200}) || null,
          website: place.website || '',
          phone: place.formatted_phone_number || '',
          isOpen: place.opening_hours?.isOpen() || null,
          analytics: generateMockAnalytics(place),
          types: place.types || [],
          emoji: getBusinessEmoji(place)
        }));
        
        setSearchResults(formattedResults);
      } else {
        setSearchResults([]);
      }
    });
  }, [placesService]);

  const generateMockAnalytics = (place) => {
    const baseViews = Math.floor(Math.random() * 5000) + 500;
    return {
      views: baseViews,
      clicks: Math.floor(baseViews * 0.15),
      calls: Math.floor(baseViews * 0.08),
      directions: Math.floor(baseViews * 0.25),
      websiteClicks: Math.floor(baseViews * 0.12),
      photoViews: Math.floor(baseViews * 0.8),
      searchImpressions: Math.floor(baseViews * 2.5),
      conversionRate: (Math.random() * 15 + 5).toFixed(1),
      averageRating: place.rating || (Math.random() * 2 + 3).toFixed(1),
      responseRate: Math.floor(Math.random() * 30 + 70),
      responseTime: `${Math.floor(Math.random() * 23 + 1)} hours`
    };
  };

  const getBusinessEmoji = (place) => {
    const name = place.name.toLowerCase();
    const types = place.types || [];
    
    // Specific cuisine types
    if (name.includes('italian') || name.includes('pasta') || name.includes('spaghetti') || name.includes('pizza')) {
      return '🍝';
    }
    if (name.includes('chinese') || name.includes('asian')) {
      return '🥡';
    }
    if (name.includes('mexican') || name.includes('taco') || name.includes('burrito')) {
      return '🌮';
    }
    if (name.includes('japanese') || name.includes('sushi') || name.includes('ramen')) {
      return '🍣';
    }
    if (name.includes('indian') || name.includes('curry')) {
      return '🍛';
    }
    if (name.includes('burger') || name.includes('grill')) {
      return '🍔';
    }
    if (name.includes('pizza')) {
      return '🍕';
    }
    if (name.includes('bakery') || name.includes('bread') || name.includes('donut')) {
      return '🥖';
    }
    
    // Business types
    if (types.includes('restaurant') || name.includes('restaurant') || name.includes('bistro') || name.includes('diner')) {
      return '🍽️';
    }
    if (types.includes('gym') || types.includes('fitness') || name.includes('gym') || name.includes('fitness')) {
      return '💪';
    }
    if (types.includes('grocery_or_supermarket') || types.includes('supermarket') || name.includes('grocery') || name.includes('market')) {
      return '🛒';
    }
    if (name.includes('ice cream') || name.includes('gelato') || types.includes('ice_cream')) {
      return '🍦';
    }
    if (types.includes('beauty_salon') || name.includes('nail') || name.includes('manicure')) {
      return '💅';
    }
    if (types.includes('hair_care') || name.includes('hair') || name.includes('salon') || name.includes('barber')) {
      return '✂️';
    }
    if (types.includes('cafe') || name.includes('coffee') || name.includes('cafe')) {
      return '☕';
    }
    if (types.includes('gas_station') || name.includes('gas') || name.includes('fuel')) {
      return '⛽';
    }
    if (types.includes('pharmacy') || name.includes('pharmacy') || name.includes('drug')) {
      return '💊';
    }
    if (types.includes('clothing_store') || types.includes('shoe_store') || name.includes('clothing') || name.includes('fashion')) {
      return '👕';
    }
    if (types.includes('bank') || name.includes('bank') || name.includes('credit')) {
      return '🏦';
    }
    if (types.includes('hospital') || name.includes('hospital') || name.includes('medical')) {
      return '🏥';
    }
    if (types.includes('school') || name.includes('school') || name.includes('education')) {
      return '🎓';
    }
    if (types.includes('dentist') || name.includes('dental')) {
      return '🦷';
    }
    if (types.includes('veterinary_care') || name.includes('vet') || name.includes('animal')) {
      return '🐕';
    }
    if (name.includes('flower') || name.includes('florist')) {
      return '🌸';
    }
    if (name.includes('hotel') || name.includes('motel') || types.includes('lodging')) {
      return '🏨';
    }
    if (name.includes('car wash') || name.includes('auto')) {
      return '🚗';
    }
    
    // Default business emoji
    return '📍';
  };

  const determineBusinessType = (place) => {
    const name = place.name.toLowerCase();
    const types = place.types || [];
    
    // Industry mapping based on common keywords and Google Place types
    if (types.includes('restaurant') || name.includes('restaurant') || name.includes('pizza') || name.includes('italian') || name.includes('bistro')) {
      return 'restaurant';
    }
    if (types.includes('gym') || types.includes('fitness') || name.includes('gym') || name.includes('fitness')) {
      return 'gym';
    }
    if (types.includes('grocery_or_supermarket') || types.includes('supermarket') || name.includes('grocery') || name.includes('market')) {
      return 'grocery store';
    }
    if (name.includes('ice cream') || name.includes('gelato') || types.includes('ice_cream')) {
      return 'ice cream shop';
    }
    if (types.includes('beauty_salon') || name.includes('nail') || name.includes('manicure')) {
      return 'nail salon';
    }
    if (types.includes('hair_care') || name.includes('hair') || name.includes('salon') || name.includes('barber')) {
      return 'hair salon';
    }
    if (types.includes('cafe') || name.includes('coffee') || name.includes('cafe')) {
      return 'cafe';
    }
    if (types.includes('gas_station') || name.includes('gas') || name.includes('fuel')) {
      return 'gas station';
    }
    if (types.includes('pharmacy') || name.includes('pharmacy') || name.includes('drug')) {
      return 'pharmacy';
    }
    if (types.includes('clothing_store') || types.includes('shoe_store') || name.includes('clothing') || name.includes('fashion')) {
      return 'retail store';
    }
    
    // Default to general category
    return types[0]?.replace('_', ' ') || 'business';
  };

  const searchCompetitors = useCallback(async (selectedBusiness) => {
    if (!selectedBusiness || !placesService) return;

    setIsSearchingCompetitors(true);
    
    // Determine the business type/industry
    const businessType = determineBusinessType(selectedBusiness);
    
    const request = {
      location: selectedBusiness.position,
      radius: 5000, // 5km radius
      type: businessType === 'restaurant' ? 'restaurant' : 
            businessType === 'gym' ? 'gym' :
            businessType === 'grocery store' ? 'grocery_or_supermarket' :
            businessType === 'ice cream shop' ? 'restaurant' :
            businessType === 'nail salon' ? 'beauty_salon' :
            businessType === 'hair salon' ? 'hair_care' :
            businessType === 'cafe' ? 'cafe' :
            businessType === 'gas station' ? 'gas_station' :
            businessType === 'pharmacy' ? 'pharmacy' :
            'establishment',
      fields: ['place_id', 'name', 'geometry', 'formatted_address', 'rating', 'user_ratings_total', 'price_level', 'photos', 'opening_hours', 'website', 'formatted_phone_number', 'types']
    };

    placesService.nearbySearch(request, (results, status) => {
      setIsSearchingCompetitors(false);
      
      if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
        // Filter out the selected business itself and format results
        const competitors = results
          .filter(place => place.place_id !== selectedBusiness.id)
          .slice(0, 20)
          .map(place => ({
            id: place.place_id,
            name: place.name,
            address: place.formatted_address,
            rating: place.rating || 0,
            reviewCount: place.user_ratings_total || 0,
            priceLevel: place.price_level || 0,
            position: {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            },
            photo: place.photos?.[0]?.getUrl({maxWidth: 300, maxHeight: 200}) || null,
            website: place.website || '',
            phone: place.formatted_phone_number || '',
            isOpen: place.opening_hours?.isOpen() || null,
            analytics: generateMockAnalytics(place),
            businessType: determineBusinessType(place),
            types: place.types || []
          }));
        
        setCompetitorResults(competitors);
        setShowCompetitors(true);
      } else {
        setCompetitorResults([]);
      }
    });
  }, [placesService]);

  // Auto-search effect with debounced query
  useEffect(() => {
    if (debouncedSearchQuery.trim() && placesService) {
      searchBusinesses(debouncedSearchQuery.trim());
    } else if (!debouncedSearchQuery.trim()) {
      setSearchResults([]);
    }
  }, [debouncedSearchQuery, placesService, searchBusinesses]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      searchBusinesses(searchQuery.trim());
    }
  };

  const selectSearchResult = (business) => {
    setSelectedSearchResult(business);
    setCenter(business.position);
    if (map) {
      map.panTo(business.position);
      map.setZoom(16);
    }
  };

  const closeLoginOverlay = () => {
    setShowLoginOverlay(false);
  };

  const openLoginOverlay = () => {
    setShowLoginOverlay(true);
  };

  const handleLogin = async (provider, email = null) => {
    // Waitlist mode: join only — do not close on join (overlay shows confirmation)
    if (WAITLIST_ONLY) {
      await joinWaitlist(email || provider);
      return;
    }
    await login(provider, email);
    setShowLoginOverlay(false);
  };

  const handleJoinWaitlist = async (email) => {
    await joinWaitlist(email);
    // Keep overlay open so LoginOverlay can show "You're on the waitlist"
  };

  const handleOpenStrategy = (prompt) => {
    if (WAITLIST_ONLY) {
      setShowLoginOverlay(true);
      return;
    }
    setStrategyPrompt(prompt);
    setActiveNavItemSafe('strategy');
  };

  return (
    <div className="app-container">
      <Header 
        activeNavItem={activeNavItem}
        setActiveNavItem={setActiveNavItemSafe}
        isAuthenticated={authed}
        logout={logout}
        onLoginClick={openLoginOverlay}
      />

      <div className="main-content">
        <div className="content-wrapper">
          {activeNavItem === 'overview' && (
            <OverviewPage 
              setActiveNavItem={setActiveNavItemSafe} 
              isAuthenticated={authed}
              login={handleLogin}
              joinWaitlist={handleJoinWaitlist}
              onOpenStrategy={handleOpenStrategy}
              onWaitlistClick={openLoginOverlay}
            />
          )}
          
          {/* Product surfaces hard-gated: never render while waitlist-only */}
          {activeNavItem === 'local-optimize' && authed && (
            <OptimizeLocalPage
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              searchResults={searchResults}
              selectedSearchResult={selectedSearchResult}
              setSelectedSearchResult={setSelectedSearchResult}
              competitorResults={competitorResults}
              showCompetitors={showCompetitors}
              isSearchingCompetitors={isSearchingCompetitors}
              currentBusiness={currentBusiness}
              handleSearchSubmit={handleSearchSubmit}
              selectSearchResult={selectSearchResult}
              searchCompetitors={searchCompetitors}
              setShowCompetitors={setShowCompetitors}
              setCompetitorResults={setCompetitorResults}
              getBusinessEmoji={getBusinessEmoji}
              map={map}
              setMap={setMap}
              center={center}
              placesService={placesService}
              setPlacesService={setPlacesService}
              mapLoaded={mapLoaded}
              setMapLoaded={setMapLoaded}
              onLoad={onLoad}
              onUnmount={onUnmount}
              mapContainerStyle={mapContainerStyle}
              mapOptions={mapOptions}
            />
          )}
          
          {activeNavItem === 'how-it-works' && (
            <HowItWorksPage />
          )}

          {activeNavItem === 'pricing' && (
            <PricingPage onWaitlistClick={openLoginOverlay} />
          )}
          
          {activeNavItem === 'faq' && (
            <FAQPage />
          )}
          
          {activeNavItem === 'dashboard' && authed && (
            <DashboardPage userProfile={userProfile} />
          )}
          
          {activeNavItem === 'create' && authed && (
            <CreatePage />
          )}
          
          {activeNavItem === 'workflows' && authed && (
            <StrategyCanvas 
              initialPrompt=""
              inline={false}
              onClose={() => setActiveNavItemSafe('overview')}
            />
          )}
          
          {activeNavItem === 'chatbot' && authed && (
            <ChatBotPage />
          )}
          
          {activeNavItem === 'strategy' && authed && (
            <StrategyCanvas 
              initialPrompt={strategyPrompt}
              inline={false}
              onClose={() => setActiveNavItemSafe('overview')}
            />
          )}
          
          {activeNavItem === 'privacy' && (
            <PrivacyPolicyPage />
          )}
          
          {activeNavItem === 'terms' && (
            <TermsOfServicePage />
          )}
          
          <LoginOverlay
            isAuthenticated={authed}
            error={error}
            loading={loading}
            login={handleLogin}
            joinWaitlist={handleJoinWaitlist}
            onClose={closeLoginOverlay}
            showOverlay={showLoginOverlay}
            defaultMode="signup"
          />
        </div>
        
        {activeNavItem !== 'workflows' && activeNavItem !== 'strategy' && activeNavItem !== 'create' && (
          <Footer 
            activeNavItem={activeNavItem}
            setActiveNavItem={setActiveNavItemSafe}
          />
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;